import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon, X } from "lucide-react"
import { cn } from "../lib/utils"
import { Button } from "./button"
import { Calendar } from "./calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"
import { Label } from "./label"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip"
import { Info } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select"

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))

/**
 * DatePicker - Modern date picker using shadcn Calendar component
 * 
 * @param {string} title - Label text
 * @param {string} name - Input name
 * @param {Date} value - Selected date value
 * @param {function} setValue - Change handler (receives date, name)
 * @param {string} errorMsg - Error message
 * @param {Date} maxDate - Maximum selectable date (fromDate for Calendar)
 * @param {Date} minDate - Minimum selectable date (toDate for Calendar)
 * @param {boolean} inline - Inline layout
 * @param {string} ids - Input ID
 * @param {string} info - Tooltip text
 * @param {boolean} disabled - Disabled state
 * @param {boolean} noInfo - Hide tooltip
 * @param {string} placeholder - Placeholder text (default: "Pick a date")
 */
export function DatePicker({
  title,
  name,
  value,
  setValue,
  errorMsg,
  maxDate,
  minDate,
  inline,
  ids,
  info,
  disabled,
  noInfo,
  placeholder = "Pick a date",
  showTimeSelect = false,
  minTime,
  maxTime,
  className,
  ...props
}) {
  const [date, setDate] = React.useState(value)
  const [timeValue, setTimeValue] = React.useState(() => {
    if (value && showTimeSelect) {
      const d = new Date(value)
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
    return ''
  })
  const [open, setOpen] = React.useState(false)

  React.useEffect(() => {
    setDate(value)
    if (value && showTimeSelect) {
      const d = new Date(value)
      setTimeValue(`${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`)
    } else if (!value) {
      setTimeValue('')
    }
  }, [value, showTimeSelect])

  const mergeDateTime = (d, time) => {
    if (!d) return null
    const result = new Date(d)
    if (time) {
      const [h, m] = time.split(':').map(Number)
      result.setHours(h, m, 0, 0)
    }
    return result
  }

  const handleSelect = (selectedDate) => {
    if (selectedDate) {
      const merged = mergeDateTime(selectedDate, timeValue)
      setDate(merged)
      if (setValue && typeof setValue === 'function') {
        setValue(merged, name)
      }
      if (!showTimeSelect) setOpen(false)
    }
  }

  const handleTimeChange = (type, val) => {
    const [currentH, currentM] = timeValue ? timeValue.split(':') : ['00', '00']
    const newH = type === 'h' ? val : currentH
    const newM = type === 'm' ? val : currentM
    let h = parseInt(newH), m = parseInt(newM)
    if (maxTime) {
      const maxH = maxTime.getHours(), maxM = maxTime.getMinutes()
      if (h > maxH || (h === maxH && m > maxM)) { h = maxH; m = maxM }
    }
    if (minTime) {
      const minH = minTime.getHours(), minM = minTime.getMinutes()
      if (h < minH || (h === minH && m < minM)) { h = minH; m = minM }
    }
    const newTime = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    setTimeValue(newTime)
    const merged = mergeDateTime(date, newTime)
    if (merged && setValue) setValue(merged, name)
  }

  const handleClear = (e) => {
    e.stopPropagation()
    setDate(undefined)
    setTimeValue('')
    if (setValue && typeof setValue === 'function') {
      setValue(null, name)
    }
  }

  // react-day-picker v9 uses disabled matchers instead of fromDate/toDate
  const disabledMatcher = [
    ...(minDate ? [{ before: minDate }] : []),
    ...(maxDate ? [{ after: maxDate }] : []),
    ...(disabled ? [true] : []),
  ]

  // Inline layout with calendar icon
  if (inline) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <div className="relative flex-1">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                id={ids}
                variant="outline"
                disabled={disabled}
                className={cn(
                  "justify-start text-left font-normal w-full",
                  date ? "pr-8" : "",
                  !date && "text-muted-foreground",
                  errorMsg && "border-red-500"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                {date ? format(new Date(date), showTimeSelect ? "dd/MM/yyyy HH:mm" : "dd MMM yyyy") : <span>{placeholder}</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={date}
                onSelect={handleSelect}
                defaultMonth={date}
                disabled={disabledMatcher.length ? disabledMatcher : undefined}
                initialFocus
              />
              {showTimeSelect && (
                <div className="border-t p-3 flex items-center gap-2">
                  <Select value={timeValue ? timeValue.split(':')[0] : undefined} onValueChange={(v) => handleTimeChange('h', v)}>
                    <SelectTrigger className="h-8 w-full text-xs">
                      <SelectValue placeholder="HH" />
                    </SelectTrigger>
                    <SelectContent className="max-h-48">
                      {HOURS.map(h => <SelectItem key={h} value={h} className="text-xs">{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="text-sm font-medium">:</span>
                  <Select value={timeValue ? timeValue.split(':')[1] : undefined} onValueChange={(v) => handleTimeChange('m', v)}>
                    <SelectTrigger className="h-8 w-full text-xs">
                      <SelectValue placeholder="MM" />
                    </SelectTrigger>
                    <SelectContent className="max-h-48">
                      {MINUTES.map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </PopoverContent>
          </Popover>
          {date && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Clear date"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    )
  }

  // Default layout with label and optional tooltip
  return (
    <div className={cn("space-y-2", className)}>
      {title && (
        <div className="flex items-center gap-2">
          <Label htmlFor={ids}>{title}</Label>
          {!noInfo && info && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>{info}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id={ids}
              variant="outline"
              disabled={disabled}
              className={cn(
                "w-full justify-start text-left font-normal",
                date ? "pr-8" : "",
                !date && "text-muted-foreground",
                errorMsg && "border-red-500"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
              {date ? format(new Date(date), showTimeSelect ? "dd/MM/yyyy HH:mm" : "dd MMM yyyy") : <span>{placeholder}</span>}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleSelect}
              defaultMonth={date}
              disabled={disabledMatcher.length ? disabledMatcher : undefined}
              initialFocus
            />
            {showTimeSelect && (
              <div className="border-t p-3 flex items-center gap-2">
                <Select value={timeValue ? timeValue.split(':')[0] : undefined} onValueChange={(v) => handleTimeChange('h', v)}>
                  <SelectTrigger className="h-8 w-full text-xs">
                    <SelectValue placeholder="HH" />
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {HOURS.map(h => <SelectItem key={h} value={h} className="text-xs">{h}</SelectItem>)}
                  </SelectContent>
                </Select>
                <span className="text-sm font-medium">:</span>
                <Select value={timeValue ? timeValue.split(':')[1] : undefined} onValueChange={(v) => handleTimeChange('m', v)}>
                  <SelectTrigger className="h-8 w-full text-xs">
                    <SelectValue placeholder="MM" />
                  </SelectTrigger>
                  <SelectContent className="max-h-48">
                    {MINUTES.map(m => <SelectItem key={m} value={m} className="text-xs">{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </PopoverContent>
        </Popover>
        {date && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            aria-label="Clear date"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {errorMsg && (
        <p className="text-sm text-red-500">{errorMsg}</p>
      )}
    </div>
  )
}
